import React from 'react';
import { UserGroupIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

export const ColorTestPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          Color Debugging Test Page
        </h1>
        
        {/* Hero Section Test */}
        <div className="bg-gradient-to-r from-blue-800 to-purple-800 text-white p-8 rounded-xl mb-8">
          <h2 className="text-2xl font-bold mb-6">Hero Section Color Test</h2>
          
          {/* Button Tests */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-medical-green hover:bg-medical-emerald rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-green-300 min-w-[200px]">
              <UserGroupIcon className="w-6 h-6 mr-3" aria-hidden="true" />
              Patient Portal (Medical Green)
            </button>
            
            <button className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-primary-600 bg-white hover:bg-gray-50 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-primary-300 min-w-[200px]">
              <ShieldCheckIcon className="w-6 h-6 mr-3" aria-hidden="true" />
              Staff Login (Primary 600)
            </button>
          </div>
          
          {/* Trust Badges Test */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="text-center group">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-white/30 transition-colors duration-300">
                <ShieldCheckIcon className="w-6 h-6 text-white" aria-hidden="true" />
              </div>
              <div className="text-white">
                <p className="font-semibold text-sm">HIPAA-Ready</p>
                <p className="text-xs opacity-90">Secure & Private</p>
              </div>
            </div>
          </div>
        </div>

        {/* Color Palette Test */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4">Medical Colors Test</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-medical-green rounded"></div>
                <div>
                  <p className="font-medium">bg-medical-green</p>
                  <p className="text-sm text-gray-500">#10b981</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-medical-emerald rounded"></div>
                <div>
                  <p className="font-medium">bg-medical-emerald</p>
                  <p className="text-sm text-gray-500">#059669</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-medical-teal rounded"></div>
                <div>
                  <p className="font-medium">bg-medical-teal</p>
                  <p className="text-sm text-gray-500">#14b8a6</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4">Primary Colors Test</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded"></div>
                <div>
                  <p className="font-medium text-primary-600">text-primary-600</p>
                  <p className="text-sm text-gray-500">#2563eb</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-700 rounded"></div>
                <div>
                  <p className="font-medium">bg-primary-700</p>
                  <p className="text-sm text-gray-500">#1d4ed8</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-300 rounded"></div>
                <div>
                  <p className="font-medium">bg-primary-300 (Focus Ring)</p>
                  <p className="text-sm text-gray-500">#93c5fd</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hover & Focus Test */}
        <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
          <h3 className="text-xl font-semibold mb-4">Interactive States Test</h3>
          <div className="space-y-4">
            <button className="bg-medical-green hover:bg-medical-emerald px-4 py-2 text-white rounded focus:ring-4 focus:ring-green-300">
              Hover Test: Green → Emerald
            </button>
            <button className="bg-primary-600 hover:bg-primary-700 px-4 py-2 text-white rounded focus:ring-4 focus:ring-primary-300">
              Hover Test: Primary 600 → 700
            </button>
          </div>
        </div>

        {/* CSS Variables Test */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-semibold mb-4">CSS Variables Test</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>--color-medical-green:</strong>
              <div className="w-full h-8 rounded mt-1" style={{ backgroundColor: 'var(--color-medical-green)' }}></div>
            </div>
            <div>
              <strong>--color-medical-emerald:</strong>
              <div className="w-full h-8 rounded mt-1" style={{ backgroundColor: 'var(--color-medical-emerald)' }}></div>
            </div>
            <div>
              <strong>--color-primary-600:</strong>
              <div className="w-full h-8 rounded mt-1" style={{ backgroundColor: 'var(--color-primary-600)' }}></div>
            </div>
            <div>
              <strong>--color-primary-300:</strong>
              <div className="w-full h-8 rounded mt-1" style={{ backgroundColor: 'var(--color-primary-300)' }}></div>
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="bg-gray-100 p-4 rounded-lg text-sm">
          <h4 className="font-semibold mb-2">Debug Information:</h4>
          <p>• Server running on: <strong>http://localhost:3002</strong></p>
          <p>• Tailwind CSS compiled successfully</p>
          <p>• Color classes generated in build output</p>
          <p>• CSS variables defined in design-system.css</p>
        </div>
      </div>
    </div>
  );
};